import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import WizardHeader from "./wizard/WizardHeader";
import { scrollToTop, useFocusedFlowChrome } from "./wizard/wizardScroll";
import useWindowWidth from "../../../hooks/useWindowWidth";

// Screen 5 of the public flow — the Pending approval payoff.
//
// Client Rev 1: "Screen 5 is the Pending approval payoff state. Use the Stitch
// design and copy it exactly, including the QUSHi coin image." The old shared
// payoff (LeagueCreatedSuccess, still the private flow's screen) announced a
// created league and handed over an invite code and a share link. Neither is
// true here: a public league is SUBMITTED, not live, and —
//
//   "Invite code: we are removing this feature from public leagues for now, but
//    keep the infrastructure in case we revisit it later."
//
// so no code and no share block. The infrastructure is untouched: the API still
// issues a code, LeagueCreatedSuccess still shows one for private leagues, and
// the wizard still passes it through in navigation state. This screen simply
// doesn't offer it.
//
// Sharing waits for approval too — the league's own landing page carries the
// Share icon once it is public.
const PublicLeagueSubmitted = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vanityUrl } = location.state || {};
  const containerRef = useRef(null);

  // Focused screen ON MOBILE ONLY. Client Rev 3, Screen 5: "Restore the normal
  // desktop top header with the centered QUSHi logo and the avatar,
  // notifications, and chats on the right." Same 768px switch the wizard uses,
  // so the two screens of the flow cannot end up in different shells.
  const focused = useWindowWidth() < 768;
  useFocusedFlowChrome(focused);

  // Client Rev 1: "Initial load: always open Screen 5 at the true top of the
  // page. Every screen should open at the top of the page. Today it was super
  // buggy, with some screens 1-5 opening midway down."
  useEffect(() => {
    scrollToTop(containerRef.current);
  }, []);

  // Reached without the state the submit hands over — a refresh, or a pasted
  // URL. There is no league to point at, so this is not a screen any more.
  useEffect(() => {
    if (!vanityUrl) navigate("/contest", { replace: true });
  }, [vanityUrl, navigate]);

  if (!vanityUrl) return null;

  // Client Rev 1: "Back arrow: returns to League Play Home. It must never return
  // to Step 4 or reopen the creation flow." `replace` is what closes the flow —
  // the wizard already replaced its own entry on submit, so there is no step to
  // go back to; this keeps it that way for the browser's Back button too.
  const exitToLeaguePlay = () => navigate("/contest", { replace: true });

  return (
    <div
      ref={containerRef}
      className="min-h-[calc(100vh-6rem)] bg-white"
      // Only the mobile shell has a fixed header to clear; on desktop the app's
      // own DashboardHeader offset (Contest.jsx) already does it.
      style={focused ? { paddingTop: 64 } : undefined}
    >
      {/* Back arrow only — the Stitch payoff carries no title in its header.
          Mobile only: desktop uses the browser's own back button. */}
      {focused && (
        <WizardHeader onBack={exitToLeaguePlay} backLabel="Back to League Play" />
      )}

      <div className="max-w-screen-sm mx-auto px-6 pt-6 pb-16 flex flex-col items-center text-center">
        {/* The QUSHi coin, exactly as the Stitch design has it. The PNG is
            genuinely transparent, and nothing is drawn behind it — client Rev 1:
            "Please make sure the background is transparent and that the white
            block is not behind it." */}
        <img
          src="/league-join-coin.png"
          alt=""
          className="w-48 h-48 md:w-56 md:h-56 object-contain select-none"
          draggable="false"
        />

        <h1 className="mt-8 font-montserrat font-bold text-title-subpage md:text-[2.25rem] md:leading-tight text-pageTitle">
          Public League Submitted
        </h1>
        {/* Client Rev 3, Screen 5f: "Subtitle: increase it slightly. It is
            noticeably weaker and smaller than it needs to be on desktop. Keep it
            secondary, but make it comfortably readable." 16px -> 18px on desktop;
            the secondary token and the weight are unchanged, and mobile keeps
            the size it already had. */}
        <p className="mt-3 text-base md:text-lg text-secondary">
          We're reviewing it now. We'll let you know when it's approved.
        </p>

        {/* Not sticky: this is a destination, not a step with a form below it.
            "View League: opens the newly submitted league landing page in its
            Pending approval state." */}
        {/* Client Rev 3, Screen 5c — the payoff rhythm, desktop:
              title / subtitle
              about 28 to 32 px          -> md:mt-8 (32px)
              View League button at roughly 480 px wide, height around 56 px,
              with the current rounded corner treatment
              about 24 px                -> the helper's md:mt-6 below
            Mobile keeps the full-width button it already had. */}
        <button
          type="button"
          onClick={() => navigate(`/contest/${vanityUrl}`)}
          className="mt-8 w-full md:w-[480px] md:max-w-full rounded-xl bg-[#0F4146] text-white py-3.5 md:py-0 md:h-14 font-semibold hover:bg-[#0B3A3F] transition-colors"
        >
          View League
        </button>

        {/* The league is already in My Leagues with its Pending approval status,
            so this is where the organiser picks it back up. */}
        {/* Client Rev 2: "payoff screen 5 - put 'My Leagues' on line two
            centered." Left to wrap on its own the line broke mid-phrase — "…from
            My" / "Leagues" — so the break is explicit: the sentence on line one,
            the link whole on line two. */}
        {/* Client Rev 3, Screen 5b: "'You can manage your league settings from My
            Leagues' - on desktop, this can stay centered in the content block and
            stay on one line. Keep My Leagues bold inline." So the explicit break
            Rev 2 asked for stays a MOBILE break (`block md:inline`), and desktop
            runs the sentence and its bold link on one line.
            5e: "My Leagues link should point to the My Leagues screen's Starting
            Soon tab" — it used to point at League Play home's My Leagues block. */}
        <p className="mt-5 md:mt-6 text-sm md:text-base text-secondary text-center">
          <span className="block md:inline">
            You can manage your league settings from{" "}
          </span>
          <Link
            to="/contest/my-games?tab=soon"
            className="font-semibold text-[#0F172A] hover:underline"
          >
            My Leagues
          </Link>
        </p>
      </div>
    </div>
  );
};

export default PublicLeagueSubmitted;
